import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Activity, Loader2, CheckCircle, XCircle, ArrowDownLeft, ArrowUpRight, ArrowLeftRight, ChevronLeft, ChevronRight, GripVertical, Edit3, Save, X as XIcon, RotateCcw } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { Device } from '../types';

// Custom scrollbar styles for Botoeira
const scrollbarStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    height: 8px;
  }

  .custom-scrollbar::-webkit-scrollbar-track {
    background: #111827;
    border-radius: 4px;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #374151;
    border-radius: 4px;
    border: 1px solid #1f2937;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #4b5563;
  }

  /* Firefox */
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: #374151 #111827;
  }
`;

interface BotoeiraProps {
  devices: Device[];
  activeDeviceMenu: string | null;
  setActiveDeviceMenu: (id: string | null) => void;
  setMenuPosition: (position: { x: number; y: number; placement?: 'top' | 'right' }) => void;
  deviceStates: Record<string, { state: 'idle' | 'loading' | 'success' | 'error'; action: 'trigger' | 'test' }>;
  // Edit mode props
  isEditMode?: boolean;
  onToggleEditMode?: (enabled: boolean) => void;
  onReorderDevices?: (devices: Device[]) => void;
  onSaveOrder?: () => void;
  onCancelOrder?: () => void;
  onResetOrder?: () => void;
  lightTheme?: boolean;
  /** 'horizontal' (default) = bottom-row strip with left/right scroll arrows.
   *  'vertical' = stacked list for a narrow sidebar column, scrolls with the native scrollbar. */
  orientation?: 'horizontal' | 'vertical';
}

const BotoeiraComponent: React.FC<BotoeiraProps> = ({
  devices,
  activeDeviceMenu,
  setActiveDeviceMenu,
  setMenuPosition,
  deviceStates,
  isEditMode = false,
  onToggleEditMode,
  onReorderDevices,
  onSaveOrder,
  onCancelOrder,
  onResetOrder,
  lightTheme = false,
  orientation = 'horizontal',
}) => {
  const isVertical = orientation === 'vertical';
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const onlineCount = devices.filter(d => d.status === 'online').length;

  // Check scroll position to show/hide arrows
  const checkScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
  };

  // Check scroll on mount and when devices change
  useEffect(() => {
    checkScroll();
    // Also check after a small delay to ensure rendering is complete
    const timeout = setTimeout(checkScroll, 100);
    return () => clearTimeout(timeout);
  }, [devices]);

  // Add resize observer to check scroll on container resize
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(() => {
      checkScroll();
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, []);

  // Scroll smoothly
  const scroll = (direction: 'left' | 'right') => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = 300;
    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  };

  // Drag and drop handlers (copied from CustomerTabs pattern)
  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    if (!isEditMode) return;

    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());

    const target = e.currentTarget as HTMLElement;
    setTimeout(() => {
      target.style.opacity = '0.4';
    }, 0);
  }, [isEditMode]);

  const handleDragEnd = useCallback((e: React.DragEvent) => {
    const target = e.currentTarget as HTMLElement;
    target.style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    if (!isEditMode) return;

    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  }, [isEditMode, draggedIndex]);

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex || !onReorderDevices) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Reorder devices array
    const newDevices = [...devices];
    const [draggedDevice] = newDevices.splice(draggedIndex, 1);
    newDevices.splice(dropIndex, 0, draggedDevice);

    onReorderDevices(newDevices);

    setDraggedIndex(null);
    setDragOverIndex(null);
  }, [draggedIndex, devices, onReorderDevices]);

  return (
    <>
      <style>{scrollbarStyles}</style>
      <div className={cn(
        isVertical ? "p-2 md:p-3 h-full rounded-xl border" : "px-2 py-1.5 md:px-3 md:py-2 border-t",
        lightTheme ? "bg-white border-gray-200" : "bg-black border-gray-900"
      )}>
        <div className={isVertical ? "flex flex-col gap-2 h-full min-h-0" : "flex items-center gap-2"}>
          {/* Left side: Title and count */}
          <div className={cn("flex items-center gap-1.5 text-xs flex-shrink-0", isVertical && "w-full justify-center", lightTheme ? "text-gray-500" : "text-gray-500")}>
            <Activity className="h-3.5 w-3.5" />
            <span className="font-semibold">Botoeira</span>
            {!isEditMode && <span className="text-gray-600">({onlineCount}/{devices.length})</span>}
          </div>

          {/* Edit mode controls */}
          {isEditMode ? (
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={onSaveOrder}
                className="flex items-center gap-1 px-2 py-0.5 rounded bg-green-600 hover:bg-green-700 text-white text-xs font-medium transition-colors"
                title="Salvar ordem"
              >
                <Save className="h-3 w-3" />
                <span>Salvar</span>
              </button>
              <button
                onClick={onCancelOrder}
                className="flex items-center gap-1 px-2 py-0.5 rounded bg-gray-700 hover:bg-gray-600 text-white text-xs font-medium transition-colors"
                title="Cancelar"
              >
                <XIcon className="h-3 w-3" />
                <span>Cancelar</span>
              </button>
              <button
                onClick={onResetOrder}
                className="flex items-center gap-1 px-2 py-0.5 rounded bg-orange-600 hover:bg-orange-700 text-white text-xs font-medium transition-colors"
                title="Restaurar ordem padrão"
              >
                <RotateCcw className="h-3 w-3" />
                <span>Padrão</span>
              </button>
            </div>
          ) : (
            onToggleEditMode && (
              <button
                onClick={() => onToggleEditMode(true)}
                className={cn("flex items-center justify-center px-1.5 py-0.5 rounded text-xs font-medium transition-colors flex-shrink-0",
                  lightTheme ? "bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700" : "bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-gray-200"
                )}
                title="Editar ordem dos dispositivos"
              >
                <Edit3 className="h-3 w-3" />
              </button>
            )
          )}

          {/* Scrollable container with arrows (arrows only make sense for horizontal scroll) */}
          <div className={isVertical ? "flex-1 min-h-0 min-w-0 flex flex-col gap-2" : "flex-1 flex items-center gap-2 min-w-0"}>
          {/* Left arrow */}
          {!isVertical && showLeftArrow && (
            <button
              onClick={() => scroll('left')}
              className={cn("flex-shrink-0 p-1 rounded-md border transition-all shadow-lg z-10",
                lightTheme
                  ? "bg-white hover:bg-gray-100 border-gray-300 hover:border-gray-400"
                  : "bg-gray-900/95 hover:bg-gray-800 border-gray-700 hover:border-gray-600"
              )}
            >
              <ChevronLeft className={cn("h-3.5 w-3.5", lightTheme ? "text-gray-600" : "text-gray-400")} />
            </button>
          )}

          {/* Devices — horizontal scrolling row, or vertical stacked list when orientation="vertical" */}
          <div
            ref={scrollContainerRef}
            onScroll={checkScroll}
            className={isVertical
              ? "flex-1 min-h-0 min-w-0 flex flex-col items-stretch gap-1 md:gap-1.5 overflow-y-auto pr-1 custom-scrollbar"
              : "flex-1 min-w-0 flex items-center gap-1 md:gap-1.5 overflow-x-auto pb-2 custom-scrollbar"}
            style={{
              scrollBehavior: 'smooth'
            }}
          >
          {devices.map((device, index) => {
            const deviceState = deviceStates[device.id];
            const state = deviceState?.state || 'idle';
            const action = deviceState?.action || 'trigger';

            // Direction helpers
            const direction = device.zone_direction;
            const directionLabel = direction === 'entrada' ? 'Entrada' :
                                  direction === 'saida' ? 'Saída' :
                                  direction === 'passagem' ? 'Passagem' : null;
            const DirectionIcon = direction === 'entrada' ? ArrowDownLeft :
                                 direction === 'saida' ? ArrowUpRight :
                                 direction === 'passagem' ? ArrowLeftRight : null;

            return (
              <button
                key={device.id}
                draggable={isEditMode}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                onClick={(e) => {
                  if (isEditMode) return; // Disable clicks in edit mode
                  if (state !== 'idle') return; // Prevent clicks during loading/success/error
                  const rect = e.currentTarget.getBoundingClientRect();
                  setMenuPosition(isVertical
                    ? { x: rect.right, y: rect.top + rect.height / 2, placement: 'right' }
                    : { x: rect.left + rect.width / 2, y: rect.top, placement: 'top' });
                  const deviceIdStr = String(device.id);
                  setActiveDeviceMenu(activeDeviceMenu === deviceIdStr ? null : deviceIdStr);
                }}
                disabled={state !== 'idle' || isEditMode}
                className={cn(
                  "flex items-center gap-2 px-2.5 py-1.5 md:px-3 md:py-2 rounded-lg border transition-all group relative",
                  isVertical ? "w-full min-w-0" : "flex-shrink-0",
                  state === 'idle' && !isEditMode && (lightTheme ? "bg-gray-50 hover:bg-gray-100 border-gray-200 hover:border-gray-300" : "bg-gray-950 hover:bg-gray-900 border-gray-800 hover:border-gray-700"),
                  state === 'idle' && isEditMode && (lightTheme ? "bg-gray-50 border-gray-200 cursor-grab active:cursor-grabbing" : "bg-gray-950 border-gray-800 cursor-grab active:cursor-grabbing"),
                  state === 'loading' && "bg-blue-600 border-blue-600 cursor-wait",
                  state === 'success' && "bg-green-600 border-green-600",
                  state === 'error' && "bg-red-600 border-red-600",
                  draggedIndex === index && "opacity-40",
                  dragOverIndex === index && "border-blue-500 border-2"
                )}
              >
                {/* Button ID Number — leads the row in vertical (sidebar list) mode, trails it
                    in horizontal (strip) mode, matching each layout's natural reading order. */}
                {isVertical && (
                  <div className={cn(
                    "flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold",
                    state === 'idle' && (lightTheme ? "bg-gray-100 text-gray-400 border border-gray-200" : "bg-gray-800 text-gray-500 border border-gray-700"),
                    state !== 'idle' && "bg-white/20 text-white/60"
                  )}>
                    {index + 1}
                  </div>
                )}

                {/* Drag handle - only visible in edit mode */}
                {isEditMode && (
                  <GripVertical className="h-3.5 w-3.5 text-gray-500 flex-shrink-0" />
                )}

                {/* Status Icon */}
                <div className="flex-shrink-0">
                  {state === 'idle' && (
                    <div className={cn(
                      isVertical ? "w-2 h-2 rounded-full" : "w-1.5 h-1.5 rounded-full",
                      device.status === 'online' ? "bg-green-500" : "bg-red-500"
                    )} />
                  )}
                  {state === 'loading' && (
                    <Loader2 className="h-3 w-3 text-white animate-spin" />
                  )}
                  {state === 'success' && (
                    <CheckCircle className="h-3 w-3 text-white" />
                  )}
                  {state === 'error' && (
                    <XCircle className="h-3 w-3 text-white" />
                  )}
                </div>

                {/* Device Name with Zone Badge */}
                <div className={cn("flex flex-col items-start gap-0.5 min-w-0", isVertical && "w-full")}>
                  <span className={cn(
                    "font-medium",
                    isVertical ? "text-sm truncate block w-full" : "text-xs whitespace-nowrap",
                    state === 'idle' && (lightTheme ? "text-gray-600 group-hover:text-gray-900" : "text-gray-400 group-hover:text-gray-200"),
                    state === 'loading' && "text-white",
                    state === 'success' && "text-white",
                    state === 'error' && "text-white"
                  )}>
                    {device.name}
                  </span>

                  {/* Zone Direction Flag - tiny badge below name */}
                  {state === 'idle' && (
                    <div className={cn(
                      "flex items-center gap-0.5 rounded-sm font-medium",
                      isVertical ? "px-1.5 py-0.5 text-[10px]" : "px-1 py-px text-[7px]",
                      // Solid, higher-contrast colors in light theme — the dark-theme translucent
                      // bg + light text (e.g. bg-orange-500/20 + text-orange-400) reads fine on a
                      // near-black card, but on a near-white light-theme card it's pale-on-pale
                      // and nearly unreadable (e.g. "SAÍDA" in light orange on a barely-tinted
                      // white background).
                      direction === 'entrada' && (lightTheme ? "bg-green-100 text-green-700" : "bg-green-500/20 text-green-400"),
                      direction === 'saida' && (lightTheme ? "bg-orange-100 text-orange-700" : "bg-orange-500/20 text-orange-400"),
                      direction === 'passagem' && (lightTheme ? "bg-blue-100 text-blue-700" : "bg-blue-500/20 text-blue-400"),
                      !direction && (lightTheme ? "bg-gray-100 text-gray-600" : "bg-gray-500/20 text-gray-500")
                    )}>
                      <div className={cn(
                        "w-1 h-1 rounded-full",
                        direction === 'entrada' && "bg-green-500",
                        direction === 'saida' && "bg-orange-500",
                        direction === 'passagem' && "bg-blue-500",
                        !direction && "bg-gray-500"
                      )} />
                      <span className="uppercase">{directionLabel || 'Não Configurado'}</span>
                    </div>
                  )}
                </div>

                {/* State Label */}
                {state === 'loading' && (
                  <span className="text-[10px] font-bold text-blue-200">
                    {action === 'trigger' ? 'ACIONANDO...' : 'TESTANDO...'}
                  </span>
                )}
                {state === 'success' && (
                  <span className="text-[10px] font-bold text-green-200 animate-pulse">
                    {action === 'trigger' ? 'ACIONADO' : 'FUNCIONANDO'}
                  </span>
                )}
                {state === 'error' && (
                  <span className="text-[10px] font-bold text-red-200 animate-pulse">
                    COM PROBLEMA
                  </span>
                )}

                {/* Button ID Number (horizontal mode only — vertical mode already rendered it above) */}
                {!isVertical && (
                  <div className={cn(
                    "ml-auto flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold",
                    state === 'idle' && (lightTheme ? "bg-gray-100 text-gray-400 border border-gray-200" : "bg-gray-800 text-gray-500 border border-gray-700"),
                    state !== 'idle' && "bg-white/20 text-white/60"
                  )}>
                    {index + 1}
                  </div>
                )}
              </button>
            );
          })}
          </div>

          {/* Right arrow */}
          {!isVertical && showRightArrow && (
            <button
              onClick={() => scroll('right')}
              className={cn("flex-shrink-0 p-1 rounded-md border transition-all shadow-lg z-10",
                lightTheme
                  ? "bg-white hover:bg-gray-100 border-gray-300 hover:border-gray-400"
                  : "bg-gray-900/95 hover:bg-gray-800 border-gray-700 hover:border-gray-600"
              )}
            >
              <ChevronRight className={cn("h-3.5 w-3.5", lightTheme ? "text-gray-600" : "text-gray-400")} />
            </button>
          )}
        </div>
        </div>
      </div>
    </>
  );
};


// Wrap in React.memo for performance
export const Botoeira = React.memo(BotoeiraComponent);
